import React, { useState, useEffect } from 'react';
import { X, Briefcase, MapPin, DollarSign, Calendar, FileText, Star } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

function CandidateModal({ resource, token, onClose }) {
  const [step, setStep] = useState('profile');
  const [budget, setBudget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleDone, setScheduleDone] = useState(false);

  const TIME_SLOTS = ['9:00-10:00 AM','10:00-11:00 AM','11:00-12:00 PM','12:00-1:00 PM','1:00-2:00 PM','2:00-3:00 PM','3:00-4:00 PM','4:00-5:00 PM','5:00-6:00 PM'];

  const today = new Date();
  const dates = Array.from({length:14},(_,i)=>{
    const d = new Date(today);
    d.setDate(today.getDate()+i+1);
    return d;
  }).filter(d=>d.getDay()!==0&&d.getDay()!==6);

  const submitBudget = async () => {
    if (!budget) return;
    setSubmitting(true);
    setTimeout(() => { setDone(true); setSubmitting(false); }, 1000);
  };

  const submitSchedule = async () => {
    if (!selectedDate || selectedSlots.length === 0) return;
    setScheduling(true);
    setTimeout(() => { setScheduleDone(true); setScheduling(false); }, 1000);
  };

  const toggleSlot = (slot) => {
    if (selectedSlots.includes(slot)) setSelectedSlots(selectedSlots.filter(s=>s!==slot));
    else if (selectedSlots.length < 3) setSelectedSlots([...selectedSlots, slot]);
  };

  const formatDate = (d) => d.toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'});

  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];
  const avatarColor = colors[resource.name?.charCodeAt(0)%colors.length];

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={onClose}>
      <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'580px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 40px 80px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>

        {/* Header tabs */}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'white',zIndex:1}}>
          <div style={{display:'flex',gap:'6px'}}>
            {['profile','budget', done?'actions':null].filter(Boolean).map(v=>(
              <button key={v} onClick={()=>setStep(v)}
                style={{padding:'6px 14px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'12px',fontWeight:'600',fontFamily:'inherit',
                  background:step===v?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f1f5f9',
                  color:step===v?'white':'#64748b'}}>
                {v==='profile'?'👤 Profile':v==='budget'?'💰 Budget':'🎯 Actions'}
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}><X size={20}/></button>
        </div>

        <div style={{padding:'24px'}}>
          {/* Profile Tab */}
          {step==='profile' && (
            <div>
              <div style={{display:'flex',gap:'16px',alignItems:'flex-start',marginBottom:'24px'}}>
                <div style={{width:'64px',height:'64px',borderRadius:'50%',background:avatarColor,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'800',fontSize:'24px',flexShrink:0}}>
                  {resource.name?.charAt(0).toUpperCase()}
                </div>
                <div style={{flex:1}}>
                  <h2 style={{color:'#1e293b',fontSize:'22px',fontWeight:'700',margin:'0 0 4px'}}>{resource.isAnonymous?'Anonymous':resource.name}</h2>
                  <div style={{color:'#6366f1',fontSize:'14px',fontWeight:'600',marginBottom:'8px'}}>{resource.title}</div>
                  <div style={{display:'flex',gap:'16px',flexWrap:'wrap'}}>
                    <span style={{color:'#64748b',fontSize:'13px',display:'flex',alignItems:'center',gap:'4px'}}><Briefcase size={13}/> {resource.experience} yrs exp</span>
                    {resource.location&&<span style={{color:'#64748b',fontSize:'13px',display:'flex',alignItems:'center',gap:'4px'}}><MapPin size={13}/> {resource.location}</span>}
                    <span style={{color:'#64748b',fontSize:'13px'}}>⏰ {resource.availability==='immediate'?'Immediate':resource.availability}</span>
                  </div>
                </div>
              </div>

              {resource.summary&&(
                <div style={{background:'#f8fafc',borderRadius:'10px',padding:'16px',marginBottom:'20px',border:'1px solid #e2e8f0'}}>
                  <h4 style={{color:'#1e293b',fontSize:'13px',fontWeight:'600',margin:'0 0 8px'}}>About</h4>
                  <p style={{color:'#64748b',fontSize:'13px',lineHeight:'1.6',margin:0}}>{resource.summary}</p>
                </div>
              )}

              {resource.skills?.length>0&&(
                <div style={{marginBottom:'20px'}}>
                  <h4 style={{color:'#1e293b',fontSize:'13px',fontWeight:'600',margin:'0 0 10px'}}>🛠 Skills</h4>
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    {resource.skills.map(s=><span key={s} style={{padding:'4px 12px',background:'#ede9fe',color:'#6366f1',borderRadius:'20px',fontSize:'12px',fontWeight:'500'}}>{s}</span>)}
                  </div>
                </div>
              )}

              {resource.education&&(
                <div style={{marginBottom:'20px'}}>
                  <h4 style={{color:'#1e293b',fontSize:'13px',fontWeight:'600',margin:'0 0 6px'}}>🎓 Education</h4>
                  <p style={{color:'#64748b',fontSize:'13px',margin:0}}>{resource.education}</p>
                </div>
              )}

              <div style={{display:'flex',gap:'8px',flexWrap:'wrap',padding:'16px',background:'#f8fafc',borderRadius:'10px',border:'1px solid #e2e8f0'}}>
                <div style={{flex:1,minWidth:'120px'}}>
                  <div style={{color:'#94a3b8',fontSize:'11px',fontWeight:'600'}}>RATE TYPE</div>
                  <div style={{color:'#1e293b',fontSize:'14px',fontWeight:'600',textTransform:'capitalize'}}>{resource.rateType}</div>
                </div>
                <div style={{flex:1,minWidth:'120px'}}>
                  <div style={{color:'#94a3b8',fontSize:'11px',fontWeight:'600'}}>AVAILABILITY</div>
                  <div style={{color:'#16a34a',fontSize:'14px',fontWeight:'600',textTransform:'capitalize'}}>{resource.availability}</div>
                </div>
              </div>

              <button onClick={()=>setStep('budget')}
                style={{width:'100%',padding:'12px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'600',fontFamily:'inherit',marginTop:'20px'}}>
                Interested? Set Budget →
              </button>
            </div>
          )}

          {/* Budget Tab */}
          {step==='budget' && !done && (
            <div>
              <h3 style={{color:'#1e293b',fontSize:'18px',fontWeight:'700',margin:'0 0 8px'}}>💰 Set Your Budget</h3>
              <p style={{color:'#64748b',fontSize:'13px',margin:'0 0 24px'}}>Enter the maximum budget you are willing to offer for this candidate.</p>

              <div style={{background:'#f8fafc',borderRadius:'10px',padding:'20px',marginBottom:'20px',border:'1px solid #e2e8f0'}}>
                <div style={{color:'#94a3b8',fontSize:'11px',fontWeight:'600',marginBottom:'4px'}}>CANDIDATE</div>
                <div style={{color:'#1e293b',fontSize:'16px',fontWeight:'700'}}>{resource.isAnonymous?'Anonymous':resource.name}</div>
                <div style={{color:'#6366f1',fontSize:'13px'}}>{resource.title} • {resource.experience} yrs</div>
              </div>

              <div style={{marginBottom:'24px'}}>
                <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>YOUR EXPECTED BUDGET (Rs./month) *</label>
                <input type="number" value={budget} onChange={e=>setBudget(e.target.value)}
                  placeholder="e.g. 50000"
                  style={{width:'100%',padding:'14px',background:'white',border:'2px solid #6366f1',borderRadius:'8px',color:'#1e293b',fontSize:'18px',fontWeight:'700',outline:'none',boxSizing:'border-box'}}/>
                <p style={{color:'#94a3b8',fontSize:'12px',margin:'6px 0 0'}}>This will be shared with the vendor for negotiation.</p>
              </div>

              <button onClick={submitBudget} disabled={!budget||submitting}
                style={{width:'100%',padding:'13px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'15px',fontWeight:'600',fontFamily:'inherit',opacity:!budget?0.5:1}}>
                {submitting?'Submitting...':'Submit Budget →'}
              </button>
            </div>
          )}

          {/* After budget submitted */}
          {step==='budget' && done && (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:'48px',marginBottom:'16px'}}>✅</div>
              <h3 style={{color:'#1e293b',fontSize:'20px',fontWeight:'700',margin:'0 0 8px'}}>Budget Submitted!</h3>
              <p style={{color:'#64748b',fontSize:'14px',margin:'0 0 28px'}}>Rs.{Number(budget).toLocaleString()}/month submitted for {resource.name}.</p>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <button onClick={()=>setStep('actions')}
                  style={{padding:'14px',background:'#f1f5f9',color:'#64748b',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                  <FileText size={15}/> Get AI Report (24hrs)
                </button>
                <button onClick={()=>setStep('actions')}
                  style={{padding:'14px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                  <Calendar size={15}/> Schedule Interview
                </button>
              </div>
            </div>
          )}

          {/* Actions Tab - Schedule */}
          {step==='actions' && !scheduleDone && (
            <div>
              <h3 style={{color:'#1e293b',fontSize:'18px',fontWeight:'700',margin:'0 0 8px'}}>📅 Schedule Interview</h3>
              <p style={{color:'#64748b',fontSize:'13px',margin:'0 0 20px'}}>Select a date and up to 3 time slots. Vendor will confirm one slot.</p>

              <div style={{marginBottom:'20px'}}>
                <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT DATE</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {dates.slice(0,8).map(d=>(
                    <div key={d.toISOString()} onClick={()=>setSelectedDate(d)}
                      style={{padding:'8px 12px',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                        background:selectedDate?.toDateString()===d.toDateString()?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                        color:selectedDate?.toDateString()===d.toDateString()?'white':'#1e293b',
                        border:selectedDate?.toDateString()===d.toDateString()?'none':'1px solid #e2e8f0'}}>
                      {formatDate(d)}
                    </div>
                  ))}
                </div>
              </div>

              {selectedDate&&(
                <div style={{marginBottom:'24px'}}>
                  <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT UP TO 3 SLOTS ({selectedSlots.length}/3)</label>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                    {TIME_SLOTS.map(slot=>(
                      <div key={slot} onClick={()=>toggleSlot(slot)}
                        style={{padding:'10px',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'600',textAlign:'center',
                          background:selectedSlots.includes(slot)?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                          color:selectedSlots.includes(slot)?'white':'#1e293b',
                          border:selectedSlots.includes(slot)?'none':'1px solid #e2e8f0',
                          opacity:!selectedSlots.includes(slot)&&selectedSlots.length>=3?0.4:1}}>
                        {slot}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={submitSchedule} disabled={!selectedDate||selectedSlots.length===0||scheduling}
                style={{width:'100%',padding:'13px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'600',fontFamily:'inherit',opacity:(!selectedDate||selectedSlots.length===0)?0.5:1}}>
                {scheduling?'Sending...':'Send Slots to Vendor →'}
              </button>
            </div>
          )}

          {scheduleDone&&(
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:'48px',marginBottom:'16px'}}>🎉</div>
              <h3 style={{color:'#1e293b',fontSize:'20px',fontWeight:'700',margin:'0 0 8px'}}>Slots Sent!</h3>
              <p style={{color:'#64748b',fontSize:'14px',margin:'0 0 24px'}}>Vendor will choose the best slot and confirm the meeting.</p>
              <button onClick={onClose} style={{padding:'12px 28px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'600',fontFamily:'inherit'}}>Done ✓</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BenchCandidates({ token }) {
  const [bench, setBench] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadBench(); }, []);

  const loadBench = async () => {
    try {
      const r = await fetch(`${API}/bench`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setBench(d.resources || []);
    } catch {}
  };

  const filtered = bench.filter(b => {
    const matchSearch = b.name?.toLowerCase().includes(search.toLowerCase()) ||
      b.title?.toLowerCase().includes(search.toLowerCase()) ||
      (b.skills||[]).some(s=>s.toLowerCase().includes(search.toLowerCase()));
    const matchFilter = filter==='all' || b.availability===filter;
    return matchSearch && matchFilter;
  });

  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];

  return (
    <div>
      {/* Search + Filter */}
      <div style={{display:'flex',gap:'12px',marginBottom:'24px',flexWrap:'wrap'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search by name, title, skill..."
          style={{flex:1,minWidth:'200px',padding:'10px 16px',background:'white',border:'1px solid #e2e8f0',borderRadius:'8px',color:'#1e293b',fontSize:'14px',outline:'none'}}/>
        <div style={{display:'flex',gap:'6px'}}>
          {[['all','All'],['immediate','Immediate'],['15days','15 Days'],['30days','30 Days']].map(([val,label])=>(
            <button key={val} onClick={()=>setFilter(val)}
              style={{padding:'8px 16px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit',
                background:filter===val?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f1f5f9',
                color:filter===val?'white':'#64748b'}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:'16px'}}>
        {filtered.map(r=>{
          const avatarColor = colors[r.name?.charCodeAt(0)%colors.length];
          return (
            <div key={r._id} style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #e2e8f0',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
              <div style={{display:'flex',gap:'12px',alignItems:'flex-start',marginBottom:'14px'}}>
                <div style={{width:'48px',height:'48px',borderRadius:'50%',background:avatarColor,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:'18px',flexShrink:0}}>
                  {r.name?.charAt(0).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:'#1e293b',fontWeight:'700',fontSize:'15px',marginBottom:'2px'}}>{r.isAnonymous?'Anonymous':r.name}</div>
                  <div style={{color:'#6366f1',fontSize:'13px',fontWeight:'500'}}>{r.title}</div>
                </div>
                <span style={{padding:'3px 10px',borderRadius:'20px',fontSize:'10px',fontWeight:'700',flexShrink:0,
                  background:r.availability==='immediate'?'#dcfce7':'#fef9c3',
                  color:r.availability==='immediate'?'#16a34a':'#ca8a04'}}>
                  {r.availability==='immediate'?'IMMEDIATE':r.availability?.toUpperCase()}
                </span>
              </div>

              <div style={{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'}}>
                <span style={{color:'#64748b',fontSize:'12px',display:'flex',alignItems:'center',gap:'3px'}}><Briefcase size={11}/> {r.experience} yrs</span>
                {r.location&&<span style={{color:'#64748b',fontSize:'12px',display:'flex',alignItems:'center',gap:'3px'}}><MapPin size={11}/> {r.location}</span>}
              </div>

              {r.summary&&<p style={{color:'#64748b',fontSize:'13px',lineHeight:'1.5',margin:'0 0 12px',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{r.summary}</p>}

              <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'14px'}}>
                {(r.skills||[]).slice(0,4).map(s=><span key={s} style={{padding:'2px 8px',background:'#ede9fe',color:'#6366f1',borderRadius:'4px',fontSize:'11px'}}>{s}</span>)}
                {r.skills?.length>4&&<span style={{color:'#94a3b8',fontSize:'11px',padding:'2px 4px'}}>+{r.skills.length-4} more</span>}
              </div>

              <button onClick={()=>setSelected(r)}
                style={{width:'100%',padding:'10px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',fontFamily:'inherit'}}>
                View More & Connect →
              </button>
            </div>
          );
        })}
      </div>

      {filtered.length===0&&(
        <div style={{textAlign:'center',padding:'60px',background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',color:'#94a3b8'}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}>👥</div>
          <p style={{margin:0,fontSize:'15px'}}>No candidates found</p>
        </div>
      )}

      {selected&&<CandidateModal resource={selected} token={token} onClose={()=>setSelected(null)}/>}
    </div>
  );
}
